<template>
  <div class="text-left">
    <h5 class="text-center">
      <strong>{{ $t("name") }}:</strong> {{ title }}
      <span class="ml-5" v-if="supervisor"
        ><strong> Supervisor:</strong> {{ supervisor.username }}</span
      >
    </h5>
    <v-divider />
    <form>
      <v-stepper v-model="step">
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">{{
            $t("form.basicData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="2" :complete="step > 2">{{
            $t("form.administrativeDate")
          }}</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <h5>
              {{ $t("form.givenAndFamilyName") }}
            </h5>
            <v-row>
              <v-col cols="12" sm="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.preferred_name.given_name"
                  @blur="normalizePersonNameField(investigator.person.preferred_name, 'given_name')"
                  :label="$t('form.name')"
                  required
                  outlined
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.preferred_name.family_name"
                  @blur="normalizePersonNameField(investigator.person.preferred_name, 'family_name')"
                  :label="$t('form.familyName')"
                  required
                  outlined
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.givenAndFamilyNameAlt") }}
            </h5>
            <v-row>
              <v-col cols="12" sm="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.alt_name.given_name"
                  @blur="normalizePersonNameField(investigator.person.alt_name, 'given_name')"
                  :label="$t('form.name')"
                  required
                  outlined
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" class="py-0">
                <v-text-field
                  v-model="investigator.person.alt_name.family_name"
                  @blur="normalizePersonNameField(investigator.person.alt_name, 'family_name')"
                  :label="$t('form.familyName')"
                  required
                  outlined
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <h5>{{ $t("form.email") }}</h5>
            <ArrayFieldForm
              v-model="investigator.person.email"
              :read-only="true"
              name-field="email"
              placeholder="Email .."
            />
            <h5>{{ $t("form.incharge") }}</h5>
            <SelectPeopleList
              :disabled-form="true"
              :items-selected="investigator.person.incharge"
            />
            <h5>
              {{ $t("form.publication.research_areas") }}
            </h5>
            <v-row>
              <v-col cols="12" class="py-0">
                <v-select
                  :items="researchAreas"
                  v-model="investigator.person.research_areas"
                  :label="$t('form.publication.research_areas')"
                  required
                  :item-text="'name.' + $i18n.locale"
                  item-value="id"
                  outlined
                  readonly
                  multiple
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="text-right my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
                <v-btn class="mr-4" v-if="step < 2" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-row>
              <v-col cols="12" md="6" class="py-0">
                <h5>
                  {{ $t("form.dni") }}
                </h5>
                <v-row>
                  <v-col cols="4" class="py-0">
                    <v-select
                      v-model="investigator.person.id_number.type"
                      :label="$t('type')"
                      outlined
                      :items="identificationOptions"
                      item-text="text"
                      item-value="value"
                      readonly
                    ></v-select
                  ></v-col>
                  <v-col cols="8" class="py-0">
                    <v-text-field
                      v-model="investigator.person.id_number.value"
                      :label="$t('label.value')"
                      ref="idNumberText"
                      required
                      outlined
                      readonly
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <h5>
                  {{ $t("form.mobile") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="investigator.person.number"
                      outlined
                      :label="$t('form.mobile')"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="3" class="py-0">
                <h5>{{ $t("form.gender") }}</h5>
                <v-row>
                  <v-col class="py-0 mt-1">
                    <v-btn-toggle
                      v-model="investigator.person.gender"
                      mandatory
                      disabled
                    >
                      <v-btn value="male" class="disable-events">
                        {{ $t("form.male") }}
                      </v-btn>
                      <v-btn value="female" class="disable-events">
                        {{ $t("form.female") }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="text-right my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
                <v-btn class="mr-4" v-if="step < 2" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-divider />
      <DirectorActions v-if="isAuthorize" :object_type="object_type" />
      <SupervisorActions
        v-else
        :item="investigator"
        :object_type="object_type"
        :form_name="form_name"
        :state="state"
        :supervisor="supervisor !== null ? supervisor.username : ''"
        @change-state-supervisor="onStateSupervisorChange"
      />
    </form>
  </div>
</template>

<script>
import axios from "axios";
import DirectorActions from "../../action_buttons/DirectorActions";
import SupervisorActions from "../../action_buttons/SupervisorActions";
import SelectPeopleList from "../../reusables/SelectPeopleList";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import investigatorData from "../../../mixins/investigator_data";
import investigator_model from "../../../mixins/model_utils/investigator_model";

export default {
  mixins: [investigatorData, investigator_model],
  components: {
    SelectPeopleList,
    DirectorActions,
    SupervisorActions,
    ArrayFieldForm
  },
  data: () => ({
    step: 1,
    state: "",
    reject_comment: "",
    title: "",
    form_name: "",
    object_type: "",
    supervisor: ""
  }),

  computed: {
    isAuthorize() {
      return this.$route.meta.authorize !== undefined;
    }
  },

  created() {
    this.initialize();
    this.getResearchAreas();
  },

  methods: {
    initialize() {
      let path;
      if (this.isAuthorize) {
        path =
          this.$store.state.endpoint +
          "/direction/moderatedOperations/authorize/investigator/" +
          this.$route.params.id;
      } else {
        path =
          this.$store.state.endpoint +
          "/moderatedOperations/review/investigator/" +
          this.$route.params.id;
      }
      axios
        .get(path)
        .then(response => {
          this.investigator.person = response.data.data.user.person;
          this.reject_comment = response.data.reject_comment;
          this.form_name = response.data.form_name;
          this.title = response.data.title;
          this.state = response.data.state;
          this.supervisor = response.data.supervisor;
          this.object_type = response.data.object_type;
          this.investigator.id = response.data.object_id;
          this.getInvestigatorName(
            this.$store.state.endpoint + "/person/" + this.investigator.id
          );
        })
        .catch(error => {
          console.log(error);
        });
    },

    onStateSupervisorChange(state, supervisor) {
      this.state = state;
      this.supervisor = supervisor;
    }
  }
};
</script>
